import {
  BitkitAlert,
  BitkitButton,
  BitkitDialog,
  BitkitLabeledData,
  BitkitSegmentedControl,
  BitkitSteps,
  BitkitTextInput,
  IconArrowLeft,
  IconArrowRight,
  useResponsive,
} from '@bitrise/bitkit-v2';
import { Separator } from '@chakra-ui/react/separator';
import { chakra } from '@chakra-ui/react/styled-system';
import { Text } from '@chakra-ui/react/text';
import cronstrue from 'cronstrue';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import useMonolithApiQuery from '@/hooks/api/useMonolithApiQuery';
import AppDetailsPageProps from '@/pages/App/types/AppDetailsPageProps';
import { formatDateTime } from '@/utils/commonFormats';
import { dateInputDateFormat } from '@/utils/dateUtils';
import { getScheduledNextRunPath } from '@/utils/monolithApiRouteService';
import { queryClient } from '@/utils/query';

import { ScheduledBuild } from '../../AppDetails/components/ScheduledBuilds/scheduledBuilds.types';
import parseBuildParams from '../../AppDetails/components/ScheduledBuilds/utils/parseBuildParams';
import TimeZoneDropdown, { formatTimeZoneLabel } from '../../AppDetails/components/TimeZoneDropdown/TimeZoneDropdown';
import BasicBuildSchedule from '../BuildConfiguration/BasicBuildSchedule';
import BuildConfiguration, { getFormDataToSend } from '../BuildConfiguration/BuildConfiguration';
import { NextRunErrorType, NextRunType, ScheduleBuildType, ScheduleParams } from '../StartBuildDialog/types';
import useScheduleBuild from '../StartBuildDialog/useScheduleBuild';
import { Day, daysToNumeric, numericToWeekdays } from '../StartBuildDialog/utils/daysToNumeric';
import getBuildParams from '../StartBuildDialog/utils/getBuildParams';
import getSubmitButtonState from '../StartBuildDialog/utils/getSubmitButtonState';

type NextRunInfosProps = {
  cronExpression?: string;
  isError?: boolean;
  isPending?: boolean;
  nextRun?: string;
  setupLocation: string;
};

const NextRunInfos = ({ cronExpression, isError, isPending, nextRun, setupLocation }: NextRunInfosProps) => {
  let cronString = '-';
  let nextRunString = '-';

  if (!isPending) {
    try {
      if (nextRun && cronExpression && !isError) {
        cronString = cronstrue
          .toString(cronExpression, {
            throwExceptionOnParseError: true,
          })
          .replace(' AM', 'am')
          .replace(' PM', 'pm');

        nextRunString = `${formatDateTime(nextRun, { hideSeconds: true, timeZone: setupLocation })} (${formatTimeZoneLabel(setupLocation)})`;
      }
    } catch {
      cronString = '-';
    }
  }

  return (
    <>
      <BitkitLabeledData label="Repetition" value={cronString} marginBottom="24" />
      <BitkitLabeledData label="Next build scheduled at" value={nextRunString} />
    </>
  );
};

const defaultSelectedDays: Day[] = ['MO', 'TU', 'WE', 'TH', 'FR'];

const defaultValues: ScheduleBuildType = {
  branch: '',
  setupLocation: Intl.DateTimeFormat().resolvedOptions().timeZone,
  time: '00:00',
  repetitionEnabled: true,
  repeatEvery: 1,
  repeatInterval: 'week',
  monthlyRepetition: 'day',
  yearlyRepetition: 'day',
  repeatOn: defaultSelectedDays,
  startDate: DateTime.now().toFormat(dateInputDateFormat),
  scheduleConfigType: 'basic',
  buildConfigType: 'basic',
  cronExpression: '',
  source: 'branch',
};

type ScheduleBuildDialogProps = {
  app: AppDetailsPageProps['app'];
  isOpen: boolean;
  onClose: () => void;
  scheduledBuild?: ScheduledBuild;
  buildTriggerToken: string;
  defaultBranch: string;
};

const ScheduleBuildDialog = (props: ScheduleBuildDialogProps) => {
  const { app, isOpen, onClose, scheduledBuild, buildTriggerToken, defaultBranch } = props;

  const [activeStageIndex, setActiveStageIndex] = useState<0 | 1>(0);
  const [errorMessage, setErrorMessage] = useState('');

  const { isPending: isScheduleLoading, mutate: scheduleBuild } = useScheduleBuild(
    {
      appSlug: app.slug,
      buildTriggerToken,
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({ queryKey: ['scheduledBuilds', app.slug] });
        onClose();
      },
      onError: (error) => {
        setErrorMessage(error.response?.data.message || 'Unknown error');
      },
    },
  );

  const useFormMethods = useForm<ScheduleBuildType>({
    values: {
      ...defaultValues,
      ...scheduledBuild,
      branch: defaultBranch,
      scheduleConfigType: scheduledBuild?.cronExpression ? 'cron' : 'basic',
      startDate: scheduledBuild?.startDate || defaultValues.startDate,
    },
    mode: 'onChange',
  });

  const { control, handleSubmit, setError, setValue, reset, resetField, formState, watch } = useFormMethods;

  const { errors } = formState;
  const formData = watch();
  const { cronExpression, scheduleConfigType, repeatEvery, repeatOn, setupLocation } = formData;

  const {
    data: nextRunData,
    error: nextRunError,
    isPending: isNextRunPending,
    refetch: getNextRun,
  } = useMonolithApiQuery<NextRunType, NextRunErrorType>(
    [buildTriggerToken, setupLocation],
    getScheduledNextRunPath(app.slug, {
      params: {
        build_trigger_token: buildTriggerToken,
        cron_expression: cronExpression,
        setup_location: setupLocation,
      },
    }),
    {
      enabled: false,
    },
  );

  const onSubmit: SubmitHandler<ScheduleBuildType> = (data) => {
    const dataToSend = getFormDataToSend(data);
    const buildParams = getBuildParams(dataToSend);
    const scheduleParams: ScheduleParams = {
      setupLocation: data.setupLocation,
    };

    if (scheduleConfigType === 'cron') {
      scheduleParams.cronExpression = data.cronExpression;
    } else if (scheduleConfigType === 'basic') {
      const [hourAtSetupLocation, minuteAtSetupLocation] = (data.time || '').split(':');
      scheduleParams.hourAtSetupLocation = parseInt(hourAtSetupLocation, 10);
      scheduleParams.minuteAtSetupLocation = parseInt(minuteAtSetupLocation, 10);
      scheduleParams.startDate = data.startDate;
      scheduleParams.repetitionEnabled = data.repetitionEnabled;
      if (scheduleParams.repetitionEnabled) {
        scheduleParams.repeatEvery = data.repeatEvery;
        scheduleParams.repeatInterval = data.repeatInterval;
      }
      if (scheduleParams.repeatInterval === 'week' && data.repeatOn) {
        scheduleParams.weekdays = daysToNumeric(data.repeatOn);
      }
      if (scheduleParams.repeatInterval === 'month') {
        scheduleParams.monthlyRepetition = data.monthlyRepetition;
      }
      if (scheduleParams.repeatInterval === 'year') {
        scheduleParams.yearlyRepetition = data.yearlyRepetition;
      }
    }
    scheduleBuild({
      buildParams,
      scheduleParams,
      id: scheduledBuild?.id,
      isEnabled: scheduledBuild ? scheduledBuild.isEnabled : true,
    });
  };

  let tooltipLabel = '';
  let isButtonDisabled = false;
  if (scheduleConfigType === 'basic' && repeatEvery && (repeatEvery < 1 || repeatEvery > 100)) {
    tooltipLabel = 'Repeat every must be a number between 1 and 100.';
    isButtonDisabled = true;
  }
  if (scheduleConfigType === 'basic' && !repeatOn?.length) {
    tooltipLabel = 'Please select a day.';
    isButtonDisabled = true;
  }
  if (scheduleConfigType === 'cron' && !cronExpression) {
    tooltipLabel = 'Enter a cron expression first.';
    isButtonDisabled = true;
  }
  if ((scheduleConfigType === 'cron' && !nextRunData?.nextRun) || !!nextRunError) {
    isButtonDisabled = true;
  }

  useEffect(() => {
    if (scheduleConfigType === 'basic') {
      resetField('cronExpression');
    }
  }, [resetField, scheduleConfigType]);

  useEffect(() => {
    if (scheduledBuild) {
      const scheduledBuildParams = parseBuildParams(scheduledBuild.buildParams).buildParams;
      const date = DateTime.fromObject(
        {
          hour: scheduledBuild.hourAtSetupLocation,
          minute: scheduledBuild.minuteAtSetupLocation,
        },
        { zone: scheduledBuild.setupLocation },
      );

      const isAdvanced =
        scheduledBuildParams.tag ||
        scheduledBuildParams.commitHash ||
        scheduledBuildParams.stack ||
        scheduledBuildParams.machineTypeId ||
        scheduledBuildParams.environments ||
        scheduledBuildParams.licensePoolId;

      let newRepeatOn = defaultValues.repeatOn;
      if (scheduledBuild.weekdays) {
        newRepeatOn = numericToWeekdays(scheduledBuild.weekdays);
      }

      setValue('buildConfigType', isAdvanced ? 'advanced' : 'basic');

      setValue('branch', scheduledBuildParams.branch);
      setValue('commitHash', scheduledBuildParams.commitHash);
      setValue('commitMessage', scheduledBuildParams.commitMessage);
      setValue('environments', scheduledBuildParams.environments);
      setValue('licensePoolId', scheduledBuild.licensePoolId);
      setValue('machineTypeId', scheduledBuildParams.machineTypeId);
      setValue('stack', scheduledBuildParams.stack);
      setValue('tag', scheduledBuildParams.tag);
      setValue('repeatEvery', scheduledBuild.repeatEvery || defaultValues.repeatEvery);
      setValue('repeatInterval', scheduledBuild.repeatInterval || defaultValues.repeatInterval);
      setValue('repeatOn', newRepeatOn);
      setValue('monthlyRepetition', scheduledBuild.monthlyRepetition || defaultValues.monthlyRepetition);
      setValue('yearlyRepetition', scheduledBuild.yearlyRepetition || defaultValues.yearlyRepetition);
      setValue('time', scheduledBuild.hourAtSetupLocation !== undefined ? date.toFormat('HH:mm') : defaultValues.time);

      if (scheduledBuildParams.workflowId) {
        setValue('pipelineableId', `workflow#${scheduledBuildParams.workflowId}`);
      } else if (scheduledBuildParams.pipelineId) {
        setValue('pipelineableId', `pipeline#${scheduledBuildParams.pipelineId}`);
      } else if (!scheduledBuildParams.pipelineId && !scheduledBuildParams.workflowId) {
        setValue('pipelineableId', 'Based on trigger map');
      }
    }
  }, [getNextRun, scheduledBuild, setValue]);

  useEffect(() => {
    if (cronExpression) {
      queryClient.cancelQueries({ queryKey: [buildTriggerToken, setupLocation] });
      const timeoutId = setTimeout(getNextRun, 500);
      return () => clearTimeout(timeoutId);
    }
    return () => {};
  }, [buildTriggerToken, cronExpression, getNextRun, setupLocation]);

  useEffect(() => {
    if (nextRunError?.response?.data?.error) {
      setError('cronExpression', { message: nextRunError.response?.data.error });
    }
  }, [nextRunError, setError]);

  useEffect(() => {
    if (isOpen === false) {
      reset({
        ...defaultValues,
        branch: defaultBranch,
      });
      setActiveStageIndex(0);
      setErrorMessage('');
    }
  }, [defaultBranch, isOpen, reset]);

  const { isMobile } = useResponsive();

  const { disabledText, isSubmitDisabled } = getSubmitButtonState(formData);

  return (
    <BitkitDialog
      data-testid="schedule-build-dialog"
      footerButtons={
        activeStageIndex === 0 ? (
          <>
            <BitkitButton variant="tertiary" form="schedule_build_form" marginInlineEnd="auto" onClick={onClose}>
              Cancel
            </BitkitButton>
            <BitkitButton
              disabledReason={tooltipLabel}
              state={isButtonDisabled ? 'disabled' : undefined}
              suffixIcon={IconArrowRight}
              onClick={() => setActiveStageIndex(1)}
            >
              Next
            </BitkitButton>
          </>
        ) : (
          <>
            <BitkitButton variant="tertiary" form="schedule_build_form" marginInlineEnd="auto" onClick={onClose}>
              Cancel
            </BitkitButton>
            <BitkitButton variant="secondary" icon={IconArrowLeft} onClick={() => setActiveStageIndex(0)}>
              Previous
            </BitkitButton>
            <BitkitButton
              disabledReason={disabledText}
              data-testid="schedule-build"
              form="schedule_build_form"
              state={isScheduleLoading ? 'loading' : isSubmitDisabled ? 'disabled' : undefined}
              type="submit"
            >
              {scheduledBuild ? 'Save' : 'Schedule build'}
            </BitkitButton>
          </>
        )
      }
      open={isOpen}
      scrollBehavior={isMobile ? 'inside' : 'outside'}
      title={scheduledBuild ? 'Edit scheduled build' : 'Schedule build'}
      onOpenChange={({ open }) => !open && onClose()}
    >
      <BitkitSteps step={activeStageIndex}>
        <BitkitSteps.Item
          action={activeStageIndex === 1 ? { onClick: () => setActiveStageIndex(0) } : undefined}
          label="Schedule"
        />
        <BitkitSteps.Item label="Configure" />
      </BitkitSteps>
      <Separator />
      {errorMessage && <BitkitAlert variant="critical" messageText={errorMessage} />}
      <FormProvider {...useFormMethods}>
        <chakra.form id="schedule_build_form" onSubmit={handleSubmit(onSubmit)}>
          {activeStageIndex === 0 ? (
            <>
              <Text textStyle="heading/h4" marginBottom="24">
                Set up scheduling cadence
              </Text>
              <BitkitSegmentedControl
                data-testid="segmented-control"
                onValueChange={({ value }) => setValue('scheduleConfigType', value as 'basic' | 'cron')}
                value={scheduleConfigType}
                marginBlockEnd="24"
                size="lg"
                fullWidth
              >
                <BitkitSegmentedControl.Item value="basic">Basic</BitkitSegmentedControl.Item>
                <BitkitSegmentedControl.Item value="cron">Cron</BitkitSegmentedControl.Item>
              </BitkitSegmentedControl>
              {scheduleConfigType === 'basic' && <BasicBuildSchedule />}
              {scheduleConfigType === 'cron' && (
                <>
                  <Controller
                    control={control}
                    name="setupLocation"
                    render={({ field }) => (
                      <TimeZoneDropdown
                        data-testid="timezone"
                        locale={field.value || defaultValues.setupLocation}
                        onTimeZoneChange={field.onChange}
                        marginBlockEnd="24"
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="cronExpression"
                    render={({ field }) => (
                      <BitkitTextInput
                        data-testid="cron-expression"
                        label="Cron schedule expressions"
                        placeholder="Enter a cron expression"
                        marginBottom={errors.cronExpression ? '12' : '32'}
                        errorText={errors.cronExpression?.message}
                        inputProps={{ ...field, ...{ 'data-testid': 'cron-expression' } }}
                      />
                    )}
                  />
                  <NextRunInfos
                    cronExpression={cronExpression}
                    isError={!!nextRunError}
                    isPending={isNextRunPending}
                    nextRun={nextRunData?.nextRun}
                    setupLocation={setupLocation}
                  />
                </>
              )}
            </>
          ) : (
            <BuildConfiguration app={app} buildTriggerToken={buildTriggerToken} showCommands={false} />
          )}
        </chakra.form>
      </FormProvider>
    </BitkitDialog>
  );
};

export default ScheduleBuildDialog;
